/**
 * Annotated image export: draws the photo at its natural size with the same overlay as
 * CranialCanvas (contour, measurement lines including the ear-to-ear width, the N / O landmarks,
 * the derived A point, P (derived or manual), scale) plus a label box with
 * the values. Pure browser API (Image + canvas), no React.
 */
import { COLORS, LANDMARK_KEYS, LANDMARK_LABELS, MEASUREMENT_LINES } from "../components/editor/editorColors";
import { formatCva, formatIndex, formatMm, formatPx } from "./formatMeasurement";
import { classifyMeasurements } from "./cranialClassification";

const DISCLAIMER = "Análise craniana por foto - uso experimental, não é diagnóstico";
const JPEG_QUALITY = 0.92;

const isPoint = (p) => p && Number.isFinite(p.x) && Number.isFinite(p.y);

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Não foi possível carregar a imagem para exportação."));
    img.src = url;
  });
}

function strokeWithHalo(ctx, drawPath, color, lineWidth, dash = []) {
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  drawPath();
  ctx.strokeStyle = COLORS.halo;
  ctx.lineWidth = lineWidth * 2.2;
  ctx.setLineDash([]);
  ctx.stroke();
  drawPath();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.setLineDash(dash);
  ctx.stroke();
  ctx.restore();
}

function drawDot(ctx, p, r, fill, stroke, lineWidth) {
  ctx.beginPath();
  ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = lineWidth;
    ctx.stroke();
  }
}

function labelLines(measurements, calibration) {
  const rows = [];
  if (measurements && measurements.ok) {
    const { px, mm, indices } = measurements;
    MEASUREMENT_LINES.forEach((def) => {
      const value = mm ? `${formatMm(mm[def.key])} (${formatPx(px[def.key])})` : formatPx(px[def.key]);
      rows.push({ text: `${def.label}: ${value}`, color: def.color });
    });
    const classification = classifyMeasurements(measurements);
    rows.push({ text: `CVA: ${formatCva(mm ? mm.cva : null, px.cva)}` });
    rows.push({
      text: `CVAI: ${formatIndex(indices.cvai)}${classification ? ` (${classification.cvai.label})` : ""}`,
    });
    rows.push({
      text: `IC: ${formatIndex(indices.ci)}${classification ? ` (${classification.ci.label})` : ""}`,
    });
    rows.push({
      text: mm ? `Escala: ${calibration && calibration.status === "detected" ? "detectada" : "manual"}` : "Sem escala: medidas em pixels",
      small: true,
    });
    if (measurements.formula) {
      const manualP = measurements.derived && measurements.derived.posteriorSource === "manual" ? ", P manual" : "";
      const protocol = measurements.protocol
        ? `Protocolo v${measurements.protocol.version} (Skully Care${manualP}) · `
        : "";
      rows.push({
        text: `${protocol}Fórmula ${measurements.formula.name} v${measurements.formula.version}`,
        small: true,
      });
    }
  }
  rows.push({ text: DISCLAIMER, small: true });
  return rows;
}

function drawLabelBox(ctx, rows, width, height) {
  const base = Math.max(14, Math.round(Math.min(width, height) / 38));
  const small = Math.round(base * 0.78);
  const pad = Math.round(base * 0.8);
  const lineGap = Math.round(base * 0.4);
  const swatch = Math.round(base * 0.9);

  ctx.save();
  ctx.textBaseline = "top";
  const fontFor = (row) => `${row.small ? 400 : 600} ${row.small ? small : base}px Inter, Arial, sans-serif`;

  let boxW = 0;
  let boxH = pad;
  rows.forEach((row) => {
    ctx.font = fontFor(row);
    const indent = row.color ? swatch + pad / 2 : 0;
    boxW = Math.max(boxW, ctx.measureText(row.text).width + indent);
    boxH += (row.small ? small : base) + lineGap;
  });
  boxW = Math.min(boxW + pad * 2, width - pad * 2);
  boxH += pad - lineGap;

  const x = pad;
  const y = height - boxH - pad;
  const radius = Math.round(base * 0.8);
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(x, y, boxW, boxH, radius);
  else ctx.rect(x, y, boxW, boxH);
  ctx.fillStyle = COLORS.labelBox;
  ctx.fill();

  let cy = y + pad;
  rows.forEach((row) => {
    const size = row.small ? small : base;
    let cx = x + pad;
    if (row.color) {
      ctx.fillStyle = row.color;
      ctx.fillRect(cx, cy + size / 2 - size * 0.12, swatch, Math.max(3, size * 0.24));
      cx += swatch + pad / 2;
    }
    ctx.font = fontFor(row);
    ctx.fillStyle = COLORS.labelText;
    ctx.globalAlpha = row.small ? 0.85 : 1;
    ctx.fillText(row.text, cx, cy, boxW - (cx - x) - pad);
    ctx.globalAlpha = 1;
    cy += size + lineGap;
  });
  ctx.restore();
}

/**
 * @param {{ imageUrl: string, width?: number, height?: number, geometry: object,
 *   calibration?: object, measurements?: object }} input `width`/`height` default to the
 *   image's natural size; the overlay is drawn in the same normalized coordinates.
 * @returns {Promise<Blob>} JPEG (quality 0.92).
 */
export async function exportAnnotatedImage({ imageUrl, width, height, geometry, calibration, measurements }) {
  const img = await loadImage(imageUrl);
  const w = width || img.naturalWidth;
  const h = height || img.naturalHeight;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);

  const toPx = (p) => ({ x: p.x * w, y: p.y * h });
  const lw = Math.max(2, Math.round(Math.max(w, h) / 450));

  const contour = (geometry?.cranialContour || []).filter(isPoint).map(toPx);
  if (contour.length >= 3) {
    const tracePolygon = () => {
      ctx.beginPath();
      contour.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
    };
    tracePolygon();
    ctx.fillStyle = COLORS.contourFill;
    ctx.fill();
    strokeWithHalo(ctx, tracePolygon, COLORS.contour, lw);
  }

  const traceSegment = (a, b) => () => {
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
  };

  const lines = measurements && measurements.ok ? measurements.lines : null;
  if (lines) {
    MEASUREMENT_LINES.forEach((def) => {
      const line = lines[def.lineKey];
      if (!line || !isPoint(line.start) || !isPoint(line.end)) return;
      strokeWithHalo(ctx, traceSegment(toPx(line.start), toPx(line.end)), def.color, lw);
    });
    if (isPoint(lines.center)) drawDot(ctx, toPx(lines.center), lw * 1.6, COLORS.center, COLORS.halo, lw * 0.6);
  }

  const landmarks = geometry?.landmarks || {};
  const derived = measurements && measurements.ok && measurements.derived ? measurements.derived : {};
  // Dashed axis extension from the nose to the anterior crossing A (the A-P part is the length line).
  if (isPoint(landmarks.nose) && isPoint(derived.anterior)) {
    strokeWithHalo(
      ctx,
      traceSegment(toPx(landmarks.nose), toPx(derived.anterior)),
      COLORS.landmark,
      Math.max(1, lw * 0.5),
      [lw * 3, lw * 2.5]
    );
  }

  const scalePoints = (calibration?.points || []).filter(isPoint).map(toPx);
  if (scalePoints.length === 2) {
    strokeWithHalo(ctx, traceSegment(scalePoints[0], scalePoints[1]), COLORS.scale, lw);
  }
  scalePoints.forEach((p) => drawDot(ctx, p, lw * 2, COLORS.scale, COLORS.contourPoint, lw * 0.6));

  const drawMarker = (key, point, r, fill, stroke) => {
    if (!isPoint(point)) return;
    const p = toPx(point);
    drawDot(ctx, p, r, fill, stroke, lw * 0.6);
    ctx.save();
    ctx.fillStyle = COLORS.landmarkText;
    ctx.font = `700 ${Math.round(r * 1.2)}px Inter, Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(LANDMARK_LABELS[key].short, p.x, p.y + r * 0.05);
    ctx.restore();
  };
  // Derived A (white) and P (white when automatic, yellow like a landmark when manual) under
  // the N / O landmarks (yellow).
  drawMarker("anterior", derived.anterior, lw * 3.5, COLORS.derived, COLORS.landmark);
  if (isPoint(landmarks.posterior)) {
    drawMarker("posterior", landmarks.posterior, lw * 4.5, COLORS.landmark, COLORS.landmarkText);
  } else {
    drawMarker("posterior", derived.posterior, lw * 3.5, COLORS.derived, COLORS.landmark);
  }
  LANDMARK_KEYS.forEach((key) => drawMarker(key, landmarks[key], lw * 4.5, COLORS.landmark, COLORS.landmarkText));

  drawLabelBox(ctx, labelLines(measurements, calibration), w, h);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Não foi possível gerar a imagem anotada."))),
      "image/jpeg",
      JPEG_QUALITY
    );
  });
}

export default exportAnnotatedImage;
